import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DeletedLineups } from './DeletedLineups';
import { ImportedLineups } from './ImportedLineups';
import { ImportLineupContent_so5LineupImportsBatch } from './__generated__/index.graphql';

type Props = {
  so5LineupImportsBatch: ImportLineupContent_so5LineupImportsBatch;
};

export const ImportLineupContent = ({ so5LineupImportsBatch }: Props) => {
  const { cancelledSo5Lineups, so5LineupImports } = so5LineupImportsBatch;
  return (
    <Vertical gap={2}>
      {so5LineupImports?.length && (
        <>
          <Horizontal>
            <BodyM bold>
              <FormattedMessage {...glossary.new} />
            </BodyM>
            <Chip
              custom={{
                background: 'var(--c-neutral-400)',
              }}
              label={`${so5LineupImports.length}`}
              size="smaller"
            />
          </Horizontal>
          <ImportedLineups importedLineups={so5LineupImports} />
        </>
      )}
      {!!cancelledSo5Lineups?.length && (
        <>
          <Horizontal>
            <BodyM bold>
              <FormattedMessage
                id="importLineup.content.deleted"
                defaultMessage="Deleted"
              />
            </BodyM>
            <Chip
              custom={{
                background: 'var(--c-neutral-400)',
              }}
              label={`${cancelledSo5Lineups.length}`}
              size="smaller"
            />
          </Horizontal>
          <Vertical>
            <DeletedLineups cancelledSo5Lineups={cancelledSo5Lineups} />
          </Vertical>
        </>
      )}
    </Vertical>
  );
};

ImportLineupContent.fragments = {
  so5LineupImportsBatch: gql`
    fragment ImportLineupContent_so5LineupImportsBatch on So5LineupImportsBatch {
      id
      so5LineupImports {
        id
        ...ImportedLineups_so5LineupImport
      }
      cancelledSo5Lineups {
        id
        ...DeletedLineups_so5Lineup
      }
    }
    ${DeletedLineups.fragments.so5Lineup}
    ${ImportedLineups.fragments.so5LineupImport}
  ` as TypedDocumentNode<ImportLineupContent_so5LineupImportsBatch>,
};
